import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit2, Trash2 } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import PortalAdminModal from '../components/PortalAdminModal';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface PortalAdmin {
  _id: string;
  contactId: string;
  displayName: string;
  email: string;
  projects: string[];
  projectIds: string[];
}

const PortalAdmins: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState<PortalAdmin[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<PortalAdmin | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<PortalAdmin | null>(null);

  // Fetch portal admins when component mounts
  useEffect(() => {
    if (workspaceId) {
      fetchPortalAdmins();
    }
  }, [workspaceId]);

  const fetchPortalAdmins = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      // Fetch contacts where isPortalAdmin is true
      const response = await api.get(`/workspaces/${workspaceId}/contacts`, {
        params: {
          isPortalAdmin: true,
          limit: 1000
        }
      });

      const contacts = response.data.contacts || [];

      // Fetch all projects to get project names
      const projectsResponse = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000
        }
      });
      const allProjects = projectsResponse.data.projects || [];

      // Map contacts to portal admins with project names
      const portalAdmins = contacts.map((contact: any) => {
        const projectNames = (contact.projects || [])
          .map((projectId: string) => {
            const project = allProjects.find((p: any) => p._id === projectId);
            return project ? project.name : null;
          })
          .filter((name: string | null) => name !== null);

        // Get primary email
        const primaryEmail = contact.emails?.find((e: any) => e.isPrimary)?.email ||
                            contact.emails?.[0]?.email || '';

        return {
          _id: contact._id,
          contactId: contact._id,
          displayName: contact.displayName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
          email: primaryEmail,
          projects: projectNames,
          projectIds: contact.projects || []
        };
      });

      console.log('Portal Admins Data:', portalAdmins);
      setAdmins(portalAdmins);
    } catch (err: any) {
      console.error('Error fetching portal admins:', err);
      toast.error('Failed to load portal admins');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleAdd = () => {
    setEditingAdmin(null);
    setShowModal(true);
  };

  const handleEdit = (admin: PortalAdmin) => {
    setEditingAdmin(admin);
    setShowModal(true);
  };

  const handleDelete = (admin: PortalAdmin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingAdmin(null);
  };

  const handleModalSave = async (contactId: string, projectIds: string[]) => {
    try {
      // Update contact to set isPortalAdmin and projects
      await api.put(`/workspaces/${workspaceId}/contacts/${contactId}`, {
        isPortalAdmin: true,
        projects: projectIds
      });

      toast.success(`Portal admin ${editingAdmin ? 'updated' : 'added'} successfully`);
      setShowModal(false);
      setEditingAdmin(null);

      // Refresh admin list
      fetchPortalAdmins();
    } catch (err: any) {
      console.error('Error saving portal admin:', err);
      toast.error(err.response?.data?.message || 'Failed to save portal admin');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!adminToDelete) return;

    try {
      // Update contact to remove isPortalAdmin and projects
      await api.put(`/workspaces/${workspaceId}/contacts/${adminToDelete.contactId}`, {
        isPortalAdmin: false,
        projects: []
      });

      toast.success('Portal admin removed successfully');
      setShowDeleteModal(false);
      setAdminToDelete(null);

      // Refresh admin list
      fetchPortalAdmins();
    } catch (err: any) {
      console.error('Error removing portal admin:', err);
      toast.error(err.response?.data?.message || 'Failed to remove portal admin');
    }
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="builders-container">
        {/* Section Header */}
        <div className="builders-section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h2 className="builders-section-title">Portal Admins</h2>
            <p className="builders-section-subtitle">
              Manage portal administrators and their permissions
            </p>
          </div>
          <button
            className="btn btn-primary btn-md"
            onClick={handleAdd}
            style={{ marginTop: '4px' }}
          >
            Add
          </button>
        </div>

        {/* Content Card */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
        }}>
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : admins.length === 0 ? (
            <div className="builders-empty">
              <p className="builders-empty-title">No portal admins yet</p>
              <p className="builders-empty-text">
                Add your first portal administrator to manage purchaser portal access
              </p>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAdd}
                style={{ marginTop: '16px' }}
              >
                Add First Admin
              </button>
            </div>
          ) : (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Display Name</th>
                    <th>Email</th>
                    <th>Projects</th>
                    <th style={{ width: '180px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin._id}>
                      <td>{admin.displayName}</td>
                      <td>{admin.email}</td>
                      <td>
                        <div style={{
                          maxWidth: '400px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {admin.projects.join(', ')}
                        </div>
                      </td>
                      <td>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'flex-end',
                          alignItems: 'center'
                        }}>
                          <button
                            onClick={() => handleEdit(admin)}
                            title="Edit"
                            style={{
                              padding: '8px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Edit2 size={18} color="#48182F" strokeWidth={2} />
                          </button>
                          <button
                            onClick={() => handleDelete(admin)}
                            title="Delete"
                            style={{
                              padding: '8px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Trash2 size={18} color="#DC2626" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Portal Admin Modal */}
      <PortalAdminModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSave={handleModalSave}
        workspaceId={workspaceId!}
        existingAdmin={editingAdmin ? {
          _id: editingAdmin._id,
          contactId: editingAdmin.contactId,
          displayName: editingAdmin.displayName,
          projectIds: editingAdmin.projectIds
        } : undefined}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && adminToDelete && (
        <div className="contacts-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="contacts-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="contacts-modal-title">Delete Portal Admin</h2>
            <p className="contacts-modal-subtitle">
              Are you sure you want to remove {adminToDelete.displayName} as a portal administrator?
              This action cannot be undone.
            </p>
            <div className="contacts-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleDeleteConfirm}
                style={{ backgroundColor: '#DC2626' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default PortalAdmins;
